import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { toastErrors } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Checkbox, Modal, toastCaller } from '@/shared/ui';

import {
  getTranscriptorErrorMessage,
  transcriptorModel,
} from '@/entities/transcriptor';

import { i18n, OrderableDocument } from '../../config';

import styles from './AddDocumentsModal.module.scss';

type AddDocumentsModalProps = {
  isOpen: boolean;
  consultationId: string;
  documents: OrderableDocument[];
  className?: string;
  onClose: () => void;
};

const getDocumentLabel = (document: OrderableDocument): string =>
  document.tab_title || document.name || document.code;

export const AddDocumentsModal: FC<AddDocumentsModalProps> = (props) => {
  const { isOpen, consultationId, documents, className, onClose } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [selectedSeriesIds, setSelectedSeriesIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggle = (seriesId: string) => () => {
    setSelectedSeriesIds((previousSeriesIds) =>
      previousSeriesIds.includes(seriesId)
        ? previousSeriesIds.filter(
            (selectedSeriesId) => selectedSeriesId !== seriesId,
          )
        : [...previousSeriesIds, seriesId],
    );
  };

  const handleClose = () => {
    setSelectedSeriesIds([]);
    onClose();
  };

  const handleSubmit = async () => {
    trackEvent({
      eventName: 'TranscriptorReportPageAddDocumentButtonClicked',
    });
    setIsSubmitting(true);

    try {
      await dispatch(
        transcriptorModel.thunks.orderConsultationDocuments({
          consultationId,
          templateSeriesIds: selectedSeriesIds,
        }),
      ).unwrap();
      handleClose();
    } catch (error) {
      const refusal = getTranscriptorErrorMessage(error);

      toastCaller({
        type: 'error',
        heading: formatMessage(toastErrors.errorHeading),
        message: formatMessage(
          refusal ?? i18n.consultationOptionsMessages.addDocumentsError,
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      className={className}
      title={formatMessage(i18n.consultationOptionsMessages.addDocumentsTitle)}
      onCancel={handleClose}
      onOk={handleSubmit}
      okButtonText={<FormattedMessage id="global.add" defaultMessage="Add" />}
      cancelButtonText={formatMessage(
        i18n.consultationOptionsMessages.cancelButton,
      )}
      okButtonProps={{
        disabled: selectedSeriesIds.length === 0,
        loading: isSubmitting,
      }}
    >
      <div className={styles.container}>
        <p className={cn('p2', styles.description)}>
          {formatMessage(
            i18n.consultationOptionsMessages.addDocumentsDescription,
          )}
        </p>
        <div className={styles.checkboxList}>
          {documents.map((document) => (
            <Checkbox
              key={document.template_series_id}
              checked={selectedSeriesIds.includes(document.template_series_id)}
              onChange={handleToggle(document.template_series_id)}
            >
              {getDocumentLabel(document)}
            </Checkbox>
          ))}
        </div>
      </div>
    </Modal>
  );
};
